import { useMutation } from '@apollo/client';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import DateField from '../connectedForm/DateInput/DateInput';
import CheckboxInput from '../connectedForm/CheckboxInput/CheckboxInput';
import { Customer } from '../../graphql/entities';
import BlurTextField from '../connectedForm/BlurTextField/BlurTextField';
import GenerateCustomerStatementMutation from '../../graphql/mutations/customer/GenerateCustomerStatementMutation.graphql';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import { useBackgroundProcess } from '../../utils/BackgroundJobs';
import WorkspaceContext from '../Context/WorkspaceContext';
import { Button, InfoButton, SuccessButton } from '../connectedForm/Button';
import getValue from 'components/connectedForm/utils/getValue';
import { ModalBottomButtonContainer } from './Styles';
import getClientMutationId from '../../utils/getClientMutationId';
import { openFormActionInNewTab } from '../../utils/CheckPopUpBlockerFunction';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import DocumentModal2 from './DocumentModal2';
import CustomerStatement from '../../graphql/entities/customer/CustomerStatement';
import ModalContext from '../Context/ModalContext';
import { useActionProps } from '../../utils/useActionProps';
import { todaysDate } from '../../utils/DateHelper';
import { BasicHeader } from '../../actions/BasicHeader';
import { HintSpan } from '../connectedForm/HintIcon';
import PropTypes from 'prop-types';
import Notification from '../Notification';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { AlertType } from '../../graphql/schema/p4types';
import FluentModal from './FluentModal';

const useFieldState = (initialState) => {
  const [state, setState] = useState(initialState);
  return [state, (event) => setState(getValue(event))];
};

const CustomerStatementNotification = ({ onClose, generationJob }) => {
  const dispatch = useDispatch();
  const [statementPrintModalOpen, setStatementPrintModalOpen] = useState(false);

  return (
    <>
      <ModalContext.Provider
        value={{
          onClose: onClose,
        }}
      >
        <DocumentModal2
          modalOpen={statementPrintModalOpen}
          multiValue={true}
          entity={CustomerStatement}
          useCustomerDefaults={true}
          title="Print Statements"
          modalWrapper={true}
          actionEntities={[]}
          overrideActionProps={() => {
            return {
              filters: [
                {
                  filterParameters: {
                    field: 'jobId',
                    value: generationJob.jobId,
                  },
                  filterOperation: 'EQUALS',
                },
              ],
              [`agedDebtReports`]: [],
              include: false,
            };
          }}
        />
      </ModalContext.Provider>
      {statementPrintModalOpen ? null : (
        <Notification
          title={'Statement generation'}
          message={'Statement generation has been completed'}
          actionButtons={[
            <InfoButton
              key={'Results option'}
              onClick={() => {
                openFormActionInNewTab(dispatch, `${CustomerStatement.workspaceUrl}${generationJob.jobId}`);
                onClose();
              }}
            >
              View Results
            </InfoButton>,
            <InfoButton
              key={'Results option'}
              onClick={() => {
                setStatementPrintModalOpen(true);
              }}
            >
              Print Statements
            </InfoButton>,
          ]}
          type={AlertType.Action}
        />
      )}
    </>
  );
};

export const CustomerStatementModal = ({ onClose, modalOpen, singleCustomer = false }) => {
  const workspaceContext = useContext(WorkspaceContext);
  const dispatch = useDispatch();
  const baseActionProps = useActionProps('customers');
  const actionProps = normalizeVariables(baseActionProps);

  const [generateStatements] = useMutation(GenerateCustomerStatementMutation, {
    onError: (data) =>
      handleComponentQueryError(data, { dispatch, entity: Customer, name: 'Could not generate statements' }),
  });

  const { dispatchToast, dismissToast } = useP4ToastController();

  const runBackgroundProcess = useBackgroundProcess({
    title: 'Generate Statements',
    onComplete: (job) => {
      if (singleCustomer) {
        openFormActionInNewTab(dispatch, `${CustomerStatement.workspaceUrl}${job.jobId}`);
      } else {
        workspaceContext.reloadData?.(true);

        const customerStatementToastId = `${job.jobId}-result`;

        dispatchToast(
          <CustomerStatementNotification
            generationJob={job}
            onClose={() => {
              dismissToast(customerStatementToastId);
            }}
          />,
          {
            title: 'Customer Statement',
            type: AlertType.Action,
            toastId: customerStatementToastId,
            intent: 'success',
          }
        );
      }
    },
  });

  const [baseDate, setBaseDate] = useFieldState(todaysDate());
  const [isRetrospective, setIsRetrospective] = useFieldState(false);
  const [ageTransactionsByDueDate, setAgeTransactionsByDueDate] = useFieldState(null);
  const [onlyIncludeCustomersWithBalanceGreaterThan, setOnlyIncludeCustomersWithBalanceGreaterThan] = useState('0.00');
  const [transactionsIncludeDate, setTransactionsIncludeDate] = useFieldState(null);

  useEffect(() => {
    if (singleCustomer) {
      setOnlyIncludeCustomersWithBalanceGreaterThan(null);
    }
  }, [singleCustomer]);

  const onClick = () => {
    generateStatements({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          customerStatementReportCriteria: {
            ...actionProps,
            retrospective: isRetrospective,
            ageTransactionsByDueDate: ageTransactionsByDueDate,
            baseDate: baseDate,
            onlyIncludeCustomersWithBalanceGreaterThan: onlyIncludeCustomersWithBalanceGreaterThan,
            transactionsIncludeDate: transactionsIncludeDate,
          },
        },
      },
    }).then((result) => {
      const job = result.data[Object.keys(result.data)[0]].result;
      runBackgroundProcess(job);
      onClose();
    });
  };

  const count = workspaceContext.selectionState.count;
  const hint =
    count > 250 ? (
      <>
        {`${count} records`}
        <br />
        <HintSpan style={{ paddingLeft: '0', paddingRight: '3px' }}>info_outline</HintSpan>
        {'Recommended limit is 250 records, exceeding this may lead to slow report times'}
      </>
    ) : (
      `${count} records`
    );

  return (
    <FluentModal isOpen={modalOpen} styles={{ main: { minWidth: '700px', paddingBottom: '60px' } }}>
      <BasicHeader secondaryTitle={hint}>{'Generate Statements'}</BasicHeader>
      <div style={{ padding: '1em' }}>
        <DateField
          dataid="baseDate"
          label="Base date for ageing"
          value={baseDate}
          inputCols={8 / 12}
          labelCols={4 / 12}
          portal={true}
          onChange={setBaseDate}
        />
        <CheckboxInput
          dataid="isRetrospective"
          label="Retrospective"
          value={isRetrospective}
          inputCols={8 / 12}
          labelCols={4 / 12}
          onChange={setIsRetrospective}
        />
        <CheckboxInput
          dataid="ageTransactionsByDueDate"
          label="Age transactions by due date"
          inputCols={8 / 12}
          labelCols={4 / 12}
          value={ageTransactionsByDueDate}
          onChange={setAgeTransactionsByDueDate}
        />
        {singleCustomer ? null : (
          <BlurTextField
            dataid="onlyIncludeCustomersWithBalanceGreaterThan"
            label="Balance greater than"
            inputCols={8 / 12}
            labelCols={4 / 12}
            value={onlyIncludeCustomersWithBalanceGreaterThan}
            onChange={setOnlyIncludeCustomersWithBalanceGreaterThan}
            enableOnChange={true}
          />
        )}
        <DateField
          dataid="transactionsIncludeDate"
          label="Include transactions after"
          inputCols={8 / 12}
          labelCols={4 / 12}
          value={transactionsIncludeDate}
          onChange={setTransactionsIncludeDate}
        />
      </div>
      <ModalBottomButtonContainer>
        <Button data-testid="statementModal-cancel" onClick={onClose}>
          Cancel
        </Button>
        <SuccessButton data-testid="statementModal-generate" onClick={onClick}>
          Generate
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

CustomerStatementModal.propTypes = {
  onClose: PropTypes.func,
  modalOpen: PropTypes.bool,
  singleCustomer: PropTypes.bool,
};

export default CustomerStatementModal;
